import type { TripSummary } from '@trippath/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { KINS } from './Family';
import { treeOf } from '../../lib/tripTree';

interface Props {
  trips: readonly TripSummary[];
  /** The trip open or chosen; none when it is none of them. */
  current: string | null;
  /** Where each trip goes when pressed. */
  to: (trip: TripSummary) => string;
  onPicked: () => void;
  /** What is said beside each name: its days unless said. */
  detail?: (trip: TripSummary) => string;
  /** Before the trips: a choice of them all. */
  first?: ReactNode;
  /** After them: what else the menu does. */
  children?: ReactNode;
}

/**
 * Trips to choose one of, the same in every menu: a family as its tree, each branch under the
 * trip it parts from with a dot in its colour, each trip's days beside its name.
 */
export function TripTree({ trips, current, to, onPicked, detail, first, children }: Props) {
  const { t } = useTranslation();
  return (
    <ul className="trip-switch">
      {first}
      {treeOf(trips).map(({ trip, depth }) => (
        <li
          key={trip.id}
          className={`kin-${String(trip.place % KINS)}`}
          style={{ paddingLeft: depth * 16 }}
        >
          <Link
            to={to(trip)}
            aria-current={trip.id === current ? 'page' : undefined}
            onClick={onPicked}
          >
            <span className="trip-switch-name">
              {(depth > 0 || trip.place > 0) && (
                <span className="trip-switch-dot" aria-hidden="true" />
              )}
              {trip.name}
            </span>
            <span>{detail ? detail(trip) : t('trips.days', { count: trip.days })}</span>
          </Link>
        </li>
      ))}
      {children}
    </ul>
  );
}
